import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { Box, TextField, InputAdornment, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SchedulingFormData } from '../hooks/useSchedulingFormSchema';

type ReminderConfigurationFormProps = {
  control: Control<SchedulingFormData>;
};

export const ReminderConfigurationForm: React.FC<
  ReminderConfigurationFormProps
> = ({ control }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('pages.questionnaireScheduling.configuration.reminders.title')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 1 }}>
        <Controller
          name="configuration.reminderCount"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type="number"
              label={t(
                'pages.questionnaireScheduling.configuration.reminders.count.label',
              )}
              error={!!error}
              helperText={error?.message || ' '}
              slotProps={{
                htmlInput: { min: 0, max: 10 },
              }}
              sx={{ flex: 1, maxWidth: 200 }}
            />
          )}
        />

        <Controller
          name="configuration.reminderDelayMinutes"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type="number"
              label={t(
                'pages.questionnaireScheduling.configuration.reminders.delayMinutes.label',
              )}
              error={!!error}
              helperText={error?.message || ' '}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {t(
                        'pages.questionnaireScheduling.configuration.reminders.delayMinutes.unit',
                      )}
                    </InputAdornment>
                  ),
                },
                htmlInput: { min: 5 },
              }}
              sx={{ flex: 1, maxWidth: 250 }}
            />
          )}
        />
      </Box>

      <Controller
        name="configuration.reminderText"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label={t(
              'pages.questionnaireScheduling.configuration.reminders.text.label',
            )}
            placeholder={t(
              'pages.questionnaireScheduling.configuration.reminders.text.placeholder',
            )}
            error={!!error}
            helperText={
              error?.message ||
              t(
                'pages.questionnaireScheduling.configuration.reminders.text.characterCount',
                { count: field.value?.length || 0, max: 60 },
              )
            }
            slotProps={{
              htmlInput: { maxLength: 60 },
            }}
            fullWidth
            multiline
            rows={2}
          />
        )}
      />
    </Box>
  );
};
